/**
 * 회사 self-service 훅 — CompanyAdmin 이 자기(활성) 회사 정보(회사명·로고)를 관리함.
 *
 * 공급사(슈퍼관리자) 프로비저닝(useProvisioning)과 분리: 회사명·로고는 회사 소유 데이터이므로
 * 회사 관리자만 자기 회사(활성 컨텍스트)를 편집함. 대상 회사 id 는 서버가 컨텍스트로 결정(미전송).
 * 로고 바이너리는 공용 파일 인프라(POST /files → GET /files/content)를 재사용하고,
 * settings.logoKey(S3 키)만 회사에 저장함.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { getAccessToken } from "@/lib/auth";
import i18n from "i18next";
import { queryKeys } from "@/lib/query-keys";

const API_BASE_URL = import.meta.env.VITE_API_URL ?? "";

/** 자기 회사 프로필(GET /companies/self) — 회사명·로고(settings.logoKey) 표시용. */
export interface CompanySelfProfile {
  id: string;
  name: string;
  plan?: string | null;
  settings?: { logoKey?: string } | null;
  isActive?: boolean;
}

/** 자기(활성) 회사 프로필 조회. */
export function useCompanySelf() {
  return useQuery({
    queryKey: ["company", "self"] as const,
    queryFn: () => apiFetch<CompanySelfProfile>("/api/companies/self"),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });
}

/**
 * 회사 self-service 정보 수정 (PUT /companies/self) — 회사명 + 로고 키.
 * logoKey 빈 문자열이면 로고 제거. onSuccess 시 me·company self·프로비저닝 목록 캐시를 무효화함.
 */
export function useUpdateCompanySelf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; logoKey?: string | null }) =>
      apiFetch<CompanySelfProfile>("/api/companies/self", { method: "PUT", body }),
    onSuccess: () => {
      // 회사명 출처는 /auth/me(companyName·companies[]) → me 무효화로 헤더·스위처 일괄 갱신.
      void qc.invalidateQueries({ queryKey: queryKeys.me.current() });
      void qc.invalidateQueries({ queryKey: ["company", "self"] });
      void qc.invalidateQueries({ queryKey: ["provisioning", "companies"] });
    },
  });
}

/**
 * 저장된 로고(S3 키)를 인증(Bearer) fetch → objectURL 로 반환함. {@code <img src>} 는 인증 헤더를 못 실으므로
 * blob 방식이 필요함. 호출자는 반환 URL 을 다 쓰면 {@code URL.revokeObjectURL} 로 해제함.
 */
export async function fetchCompanyLogoUrl(logoKey: string): Promise<string> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE_URL}/api/files/content?key=${encodeURIComponent(logoKey)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(i18n.t("queries.logoLoadFailed", { status: res.status }));
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * 활성 회사 로고 objectURL 과 회사명 — 사이드바 브랜드·표지·문서 보고서 머리말 공용.
 * 같은 logoKey 는 한 번만 받아 캐시에서 공유함(사이드바·표지·문서 보고서가 같은 이미지를 표시).
 * logoKey 별 objectURL 은 세션 동안 유지(로고 교체 시 새 키로 다시 받음). 로고가 없거나 실패하면 logoUrl=null.
 */
export function useCompanyLogo(): { logoUrl: string | null; companyName: string | null } {
  const { data: profile } = useCompanySelf();
  const logoKey = profile?.settings?.logoKey || null;
  const { data: logoUrl } = useQuery({
    queryKey: queryKeys.companyLogo.byKey(logoKey),
    queryFn: () => fetchCompanyLogoUrl(logoKey as string),
    enabled: logoKey !== null,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
  return { logoUrl: logoKey ? (logoUrl ?? null) : null, companyName: profile?.name ?? null };
}
